import { sortBy } from "lodash";
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import {
  activityUtils,
  Button,
  Callout,
  HSpace,
  RouterLink,
  SwitchButton,
  Tabs,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import ResultsTable from "@examspark/common-ui/dist/components/activities/results-table/ResultsTable";
import {
  IconInformationCircleLine,
  IconPencilLine,
  IconRefreshLine,
} from "@examspark/common-ui/dist/components/icons";
import { matrixContainsUnscoredData } from "@examspark/exam";
import { Resource } from "@examspark/types";

import {
  useExamScoreEditorByAttempts,
  shuffleSessions,
} from "../../hooks/useExamScoresByAttempts";
import EditExamScoresByAttemptsModal from "../exams/EditExamScoresByAttemptsModal";

const ForbidDataBanner = styled.div`
  border: solid 1px #ddd;
  border-radius: 0.5rem;
  background-color: #eaecff;
  padding: 1rem;
  color: #4d5177;

  svg {
    display: block;
    fill: #4d5177;
  }
`;

type TabValues = "item" | "objective";

const ActivityResults = (props: {
  examConfig?: Resource;
  examSessions?: Resource[];
  forbidStudentData?: boolean;
  showAttempts: boolean;
  hideResultsByObjectives?: boolean;
  isFetching?: boolean;
  objectiveLabel?: string;
  onRefresh?: () => void;
}) => {
  const [tab, setTab] = useState<"item" | "objective">("item");
  const [filterName, setFilterName] = useState("");
  const [isEditExamScoresModalOpen, setIsEditExamScoresModalOpen] =
    useState(false);
  const [showStudentNames, setShowStudentNames] = useState(false);
  const [viewingStudentResponseKey, setViewingStudentResponseKey] = useState<{
    examSessionId?: string;
    itemId?: string;
  }>();

  // Rapid review loads a lot of data, so we should only load it
  // when the user expresses interest in editing scores
  // once set, this should stay on
  const [shouldLoadRapidReview, setShouldLoadRapidReview] = useState(false);

  const examScoreController = useExamScoreEditorByAttempts({
    examConfig: props.examConfig,
    disableDataLoading: !shouldLoadRapidReview,
  });

  const { examConfig, examSessions } = props;
  const resultsByObjective = useMemo(() => {
    if (!examConfig || !examSessions?.length) return;
    return activityUtils.getResultsTableDataByObjective(
      examConfig,
      // if we are anonymizing students, we should
      // scramble the order of the exam sessions
      // use the exam config id to seed the randomizer
      showStudentNames
        ? sortBy(examSessions, ["name", "id"])
        : shuffleSessions(examConfig.id, examSessions),
      {
        showStudentNames,
      },
    );
  }, [examConfig, examSessions, showStudentNames]);

  // console.log("results by objective", resultsByObjective, examConfig);

  const resultsByItem = useMemo(() => {
    if (!examConfig || !examSessions?.length) return;
    return activityUtils.getResultsTableDataByItem(
      examConfig,
      // if we are anonymizing students, we should
      // scramble the order of the exam sessions
      // use the exam config id to seed the randomizer
      showStudentNames
        ? sortBy(examSessions, ["name", "id"])
        : shuffleSessions(examConfig.id, examSessions),
      {
        showStudentNames,
      },
    );
  }, [examConfig, examSessions, showStudentNames]);

  const resultsContainUnscoredData = useMemo(() => {
    if (!resultsByItem) return;
    return matrixContainsUnscoredData(resultsByItem);
  }, [resultsByItem]);

  // console.log("results unscored", resultsContainUnscoredData);
  // console.log("results by item", resultsByItem);

  return (
    <>
      <EditExamScoresByAttemptsModal
        isOpen={isEditExamScoresModalOpen}
        onRequestClose={() => {
          setViewingStudentResponseKey(undefined);
          setIsEditExamScoresModalOpen(false);
        }}
        onAfterOpen={() => {
          // Avoid loading rapid review until they express interest
          // in editing scores
          setShouldLoadRapidReview(true);

          const initialQuestionId = viewingStudentResponseKey?.itemId
            ? { id: viewingStudentResponseKey?.itemId, sk: "item#v0" }
            : null;
          const initialSessionId = viewingStudentResponseKey?.examSessionId
            ? {
                id: viewingStudentResponseKey?.examSessionId,
                sk: "exam-session",
              }
            : null;

          examScoreController.handleInit(initialQuestionId, initialSessionId);
        }}
        onSave={props.onRefresh}
        showAttempts={props.showAttempts}
        anonymizeStudents={!showStudentNames}
        examScoreController={examScoreController}
      />
      <VSpace size="2rem">
        <Tabs value={tab} onChange={(value) => setTab(value as TabValues)}>
          <Tabs.TabItem value="item">Results By Item</Tabs.TabItem>
          {!props.hideResultsByObjectives && (
            <Tabs.TabItem value="objective">
              Results By {props.objectiveLabel ?? "Objective"}
            </Tabs.TabItem>
          )}
        </Tabs>
        <VSpace>
          <Toolbar>
            <TextInput
              value={filterName}
              style={{
                flex: "1",
                cursor: !showStudentNames ? "not-allowed" : "auto",
              }}
              autoFocus
              onChange={(e) => setFilterName(e.target.value)}
              placeholder={"Filter by Name..."}
              disabled={!showStudentNames}
            />
            <Button
              title="Reload"
              isLoading={props.isFetching}
              variant="outlined"
              onClick={(e) => {
                if (e.altKey) {
                  e.stopPropagation();
                  return;
                }
                examScoreController.refresh();
                props.onRefresh?.();
              }}
              icon={
                <div style={{ width: "16px", height: "16px" }}>
                  <IconRefreshLine width="14" />
                </div>
              }
            >
              Refresh
            </Button>
            <Button
              title="Edit Scores"
              icon={<IconPencilLine />}
              variant="outlined"
              onClick={() => setIsEditExamScoresModalOpen(true)}
            >
              Scores
            </Button>
            {!props.forbidStudentData && (
              <SwitchButton
                label="Reveal Names"
                checked={showStudentNames}
                disabled={props.forbidStudentData}
                onClick={() => {
                  if (showStudentNames) {
                    setFilterName("");
                  }
                  setShowStudentNames(!showStudentNames);
                }}
              />
            )}
          </Toolbar>
          {!props.forbidStudentData && !showStudentNames && (
            <Callout>Student order is randomized when names are hidden</Callout>
          )}
          {props.forbidStudentData && (
            <ForbidDataBanner>
              <HSpace>
                <div>
                  <IconInformationCircleLine width="16" />
                </div>
                <div>
                  TCMPC prohibits collection of student data in the platform.
                  Results displayed below are anonymous.{" "}
                </div>
              </HSpace>
            </ForbidDataBanner>
          )}
          {resultsContainUnscoredData && (
            <Callout>
              These results contain responses that require manual grading.
              Ungraded responses are shaded in gray. Click Scores button (above)
              to apply scores to these responses.
            </Callout>
          )}
        </VSpace>
        {tab === "item" && !!resultsByItem && (
          <>
            <ResultsTable
              data={resultsByItem}
              onCellClick={(rowId, colId) => {
                setIsEditExamScoresModalOpen(true);
                setViewingStudentResponseKey({
                  examSessionId: rowId,
                  itemId: colId,
                });
              }}
              rowLabelCell={(item) => {
                return (
                  <RouterLink to={`./session/${item.id}`}>
                    {item.label}
                  </RouterLink>
                );
              }}
            />
          </>
        )}
        {tab === "objective" && resultsByObjective && (
          <ResultsTable
            data={resultsByObjective}
            rowLabelCell={(item) => {
              return (
                <RouterLink to={`./session/${item.id}`}>
                  {item.label}
                </RouterLink>
              );
            }}
          />
        )}
      </VSpace>
    </>
  );
};

export default ActivityResults;
